import React, { useEffect, useRef, useState } from 'react'

import { Button, Icon, IconName, Table, triggerToast, uncapitalize, useConstCallback } from '@unbounded/unbounded-components'
import { format } from 'date-fns'
import { Cell, Column, useFlexLayout } from 'react-table'

import { formatToCurrencyNumber } from '~utils/currency'
import BlockSpinner from '~components/BlockSpinner'
import { TOKEN_SETTINGS } from '~routes/TokensPage/TokensPage.consts'

import { EthereumHookState, VestingInformation } from '../EthereumConnection'
import * as Styled from './BalanceSettings.styles'

const TRANSFERS_PER_PAGE = 1000

interface Props {
  onUpdateBalance?: () => void
  ethereum: EthereumHookState
}

interface TransferRow {
  date: Date
  amount: string
  onClaim: () => void
  isClaimable: boolean
  isClaimed: boolean
  isClaiming: boolean
  isDisabled: boolean
}

const transfersColumns: Array<Column<TransferRow>> = [
  {
    id: 'date',
    Header: 'Unlock Date',
    width: 3,
    accessor: data => format(data.date, 'MMM dd, yyyy hh:mm a'),
    Cell: ({ value }: Cell) => <Styled.TransferDate>{value}</Styled.TransferDate>,
  },
  {
    id: 'status',
    Header: 'Status',
    width: 1.25,
    accessor: data => data,
    Cell: ({ value }: Cell) => (
      <Styled.TransferStatus>
        {value.isClaimed && <Styled.VestingClaimed>Claimed</Styled.VestingClaimed>}
        {value.isClaimable && <Styled.VestingUnlocked>Unlocked</Styled.VestingUnlocked>}
        {!value.isClaimable && !value.isClaimed && <Styled.VestingPending>Pending</Styled.VestingPending>}
      </Styled.TransferStatus>
    ),
  },
  {
    id: 'amount',
    Header: 'Amount',
    width: 2,
    accessor: data => data,
    Cell: ({ value }: Cell) => <Styled.TransferStatus>{value.amount}</Styled.TransferStatus>,
  },
  {
    id: 'actions',
    Header: '',
    width: 2,
    accessor: data => data,
    Cell: ({ value }: Cell) => (
      <Styled.TransferActions>
        <Button
          color="base"
          size="small"
          onClick={value.onClaim}
          disabled={value.isClaiming || value.isDisabled || !value.isClaimable}
          isLoading={value.isClaiming}
        >
          <Icon name={IconName.brand_ethereum} size="large" />
          &nbsp;Claim
        </Button>
      </Styled.TransferActions>
    ),
  },
]

const BalanceVestingTab: React.FC<Props> = ({ onUpdateBalance, ethereum }) => {
  const { connection: ethereumConnection, queryTokenVesting: ethereumQueryTokenVesting, claimVestedTokens: ethereumClaimVestedTokens } = ethereum
  const [claimingTransfer, setClaimingTransfer] = useState<VestingInformation>()
  const [isLoadingTransfers, setIsLoadingTransfers] = useState(false)
  const [isClaiming, setIsClaiming] = useState(false)
  const [page, setPage] = useState(1)
  const [transfers, setTransfers] = useState<Array<VestingInformation> | undefined>(undefined)
  const maxPage = transfers ? Math.max(Math.ceil(transfers.length / TRANSFERS_PER_PAGE), 1) : 1
  const hasPrevPage = page > 1
  const hasNextPage = page < maxPage
  const isMounted = useRef(false)

  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  const tokenAmountToNumber = useConstCallback((amount: string) => {
    let divDecimals = BigInt(1)

    for (let i = 0; i < TOKEN_SETTINGS.decimals; i += 1) {
      divDecimals *= BigInt(10)
    }

    const balance = BigInt(amount) / divDecimals

    return Number(balance)
  })

  const reloadVestingInformation = useConstCallback(() => {
    if (!ethereumConnection) {
      return
    }

    ;(async () => {
      setIsLoadingTransfers(true)
      try {
        const info = await ethereumQueryTokenVesting(ethereumConnection.provider, TOKEN_SETTINGS.vestingAddress)

        // if there is no vesting information available at all, remove it.
        if (!info.length) {
          setTransfers(undefined)
          return
        }

        const newMaxPage = Math.min(Math.ceil(info.length / TRANSFERS_PER_PAGE), 1)

        setTransfers(info)
        setPage(prevPage => Math.min(newMaxPage, prevPage))
      } catch (e) {
        console.error('Failed to query token vesting settings', e)
        triggerToast('error', `Couldn't query ${TOKEN_SETTINGS.name} vesting information.`)
      } finally {
        setIsLoadingTransfers(false)
      }
    })()
  })

  useEffect(() => {
    ;(async () => {
      if (!ethereumConnection) {
        return
      }

      reloadVestingInformation()
    })()
  }, [ethereumConnection, reloadVestingInformation])

  const humanizeError = useConstCallback((message: string) => {
    let humanized = message

    if (humanized.includes('(action')) {
      humanized = humanized.split('(action')[0].trim()
    }

    if (humanized.includes('{')) {
      humanized = humanized.split('{')[0].trim()
    }

    humanized = uncapitalize(humanized)

    return humanized
  })

  useEffect(() => {
    ;(async () => {
      if (!claimingTransfer || !ethereumConnection) {
        return
      }

      setIsClaiming(true)

      try {
        await ethereumClaimVestedTokens(ethereumConnection.provider, TOKEN_SETTINGS.vestingAddress, claimingTransfer.index)
      } catch (e: any) {
        triggerToast('error', `Couldn't claim your tokens: ${humanizeError(e.error || e.message || 'please try again.')}`)
        setIsClaiming(false)
        return
      }

      setIsClaiming(false)
      reloadVestingInformation()

      if (onUpdateBalance) {
        onUpdateBalance()
      }
    })()
  }, [claimingTransfer])

  const visualTransfers: Array<TransferRow> = (transfers || [])
    .sort((a, b) => {
      if (a.isClaimable && !b.isClaimable) {
        return -1
      }
      if (!a.isClaimable && b.isClaimable) {
        return 1
      }
      return a.date.getTime() - b.date.getTime()
    })
    .slice((page - 1) * TRANSFERS_PER_PAGE, page * TRANSFERS_PER_PAGE)
    .map(transfer => ({
      date: transfer.date,
      amount: `${formatToCurrencyNumber(tokenAmountToNumber(transfer.amount))} ${TOKEN_SETTINGS.name}s`,
      isClaimable: transfer.isClaimable,
      isClaimed: transfer.isClaimed,
      onClaim: () => setClaimingTransfer(transfer),
      isClaiming: claimingTransfer?.index === transfer.index && isClaiming,
      isDisabled: isClaiming,
    }))

  return transfers || isLoadingTransfers ? (
    <Styled.TransfersTableContainer>
      {/* eslint-disable-next-line no-nested-ternary */}
      {!isLoadingTransfers ? (
        <>
          <Styled.VestingHeader>Unlockable Tokens</Styled.VestingHeader>
          {visualTransfers.length ? (
            <Table columns={transfersColumns} data={visualTransfers} hasDynamicRows plugins={[useFlexLayout]} />
          ) : (
            <Styled.NoUnclaimedTransfers>
              <Icon name={IconName.wallet} size="large" />
              You have no unclaimed token payments right now.
            </Styled.NoUnclaimedTransfers>
          )}
          {transfers?.length && (hasPrevPage || hasNextPage) && (
            <Styled.TransfersPagination>
              {hasPrevPage && (
                <Styled.LeftPage onClick={() => setPage(prevPage => prevPage - 1)}>
                  <Icon name={IconName.chevron_left} />
                  &nbsp;Previous page
                </Styled.LeftPage>
              )}
              {hasNextPage && (
                <Styled.RightPage onClick={() => setPage(prevPage => prevPage + 1)}>
                  Next page&nbsp;
                  <Icon name={IconName.chevron_right} />
                </Styled.RightPage>
              )}
            </Styled.TransfersPagination>
          )}
        </>
      ) : (
        <BlockSpinner />
      )}
    </Styled.TransfersTableContainer>
  ) : null
}

export default BalanceVestingTab
